import { getAuth, signInWithCredential, signInWithPopup, GoogleAuthProvider, OAuthProvider } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { logAnalyticsEvent, logGoogleAdsConversion, GOOGLE_ADS_CONVERSIONS } from '../../utils/analytics';
import config from '../../config';

// Shared post-auth handling for every social provider/flow (native credential
// or web popup): user-doc creation, needsRegistrationCompletion, navigation.
// method: analytics label ('google' | 'apple'). fullNameFallback: used when the
// Firebase user has no displayName (Apple only shares the name on first authorization).
const completeSocialSignIn = async (user, navigate, setMessage, options = {}) => {
  const { method = 'google', fullNameFallback = '' } = options;

  const userDoc = doc(db, 'users', user.uid);
  const docSnap = await getDoc(userDoc);

  const isNewUser = !docSnap.exists();

  if (isNewUser) {
    let firstName = 'User';
    let lastName = '';

    const displayName = user.displayName || fullNameFallback;
    if (displayName) {
      const nameParts = displayName.trim().split(/\s+/);
      firstName = nameParts[0] || 'User';
      lastName = nameParts.slice(1).join(' ') || '';
    }

    await setDoc(userDoc, {
      email: user.email || '',
      firstName,
      lastName,
      dateOfBirth: '',
      gender: '',
      createdAt: new Date().toISOString(),
      needsRegistrationCompletion: true // Flag for incomplete social registration
    });

    // The account exists now — the same moment Register.js records an email
    // sign-up. Social sign-ups logged no sign_up success before 2026-10-12.
    logAnalyticsEvent('sign_up', { method, status: 'success' });
    logGoogleAdsConversion(GOOGLE_ADS_CONVERSIONS.SIGN_UP);

    // Redirect to registration completion instead of immediate trial activation
    if (setMessage) {
      setMessage('Account created! Please complete your registration...');
    }

    // Use navigate if available, otherwise use window.location
    if (navigate) {
      navigate('/complete-registration');
    } else {
      window.location.href = '/complete-registration';
    }
    return true;
  }

  // For existing users, continue with normal flow
  logAnalyticsEvent('login', { method, status: 'success' });

  // Navigate existing users to dashboard
  if (navigate) {
    navigate('/dashboard');
  } else {
    window.location.href = '/dashboard';
  }
  return true;
};

export const handleFirebaseSignIn = async (credential, navigate, setMessage, options = {}) => {
  try {
    const auth = getAuth();
    const result = await signInWithCredential(auth, credential);
    return await completeSocialSignIn(result.user, navigate, setMessage, options);
  } catch (error) {
    console.error('Firebase sign-in error:', error);
    if (setMessage) {
      setMessage(`Sign-in failed: ${error.message}`);
    }
    return false;
  }
};

// Browser Apple sign-in (desktop/mobile web, outside the iOS shell) via Apple's
// web auth. Requires the Services ID + key configured in the Firebase Apple
// provider. Inside the shell the native ASAuthorization flow is used instead.
export const signInWithAppleWeb = async (navigate, setMessage) => {
  try {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    const auth = getAuth();
    const result = await signInWithPopup(auth, provider);
    return await completeSocialSignIn(result.user, navigate, setMessage, { method: 'apple' });
  } catch (error) {
    if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
      return false; // user dismissed the popup — not an error
    }
    console.error('Apple sign-in error:', error);
    if (setMessage) {
      setMessage('Unable to sign in with Apple. Please try again.');
    }
    logAnalyticsEvent('login', {
      method: 'apple',
      status: 'failure',
      error_code: error.code,
      error_message: error.message
    });
    return false;
  }
};

export const initializeGoogleSignIn = (navigate, setMessage) => {
  window.handleGoogleToken = async function(token) {
    try {

      const credential = GoogleAuthProvider.credential(token);
      await handleFirebaseSignIn(credential, navigate, setMessage);
    } catch (error) {
      console.error('Google sign-in error:', error);
      if (setMessage) {
        setMessage(`Google sign-in failed: ${error.message}`);
      }
    }
  };
};

// True when running inside the iOS WKWebView shell (WebView.swift installs this handler).
export const isIOSShell = () =>
  Boolean(window.webkit?.messageHandlers?.iOSInterface);

// True when running inside the Android WebView shell (MainActivity.kt injects
// `AndroidInterface` via addJavascriptInterface). The Android shell only bridges
// Google (and force-routes any `__/auth/` navigation to native Google Sign-In),
// so the web Apple popup can't work there — callers hide Apple sign-in entirely.
export const isAndroidShell = () =>
  Boolean(window.AndroidInterface);

export const initializeAppleSignIn = (navigate, setMessage) => {
  // Called by the iOS shell (WebView.swift) after the native ASAuthorizationController
  // flow completes. rawNonce must be the unhashed nonce whose SHA256 was sent to Apple —
  // Firebase's OAuthProvider('apple.com') verifies it against the token's nonce claim.
  window.handleAppleToken = async function(idToken, rawNonce, fullName) {
    try {
      const provider = new OAuthProvider('apple.com');
      const credential = provider.credential({ idToken, rawNonce });
      await handleFirebaseSignIn(credential, navigate, setMessage, {
        method: 'apple',
        fullNameFallback: fullName || ''
      });
    } catch (error) {
      console.error('Apple sign-in error:', error);
      if (setMessage) {
        setMessage(`Apple sign-in failed: ${error.message}`);
      }
    }
  };
};